/**
 * Agent converse — the FIRST-CLASS SDK conversation control.
 *
 * Any app mounts a full agent chat surface in one line:
 *
 *   const chat = ctx.agents.converse({ agentId: "analyst.quill" });
 *   el.appendChild(chat.el);
 *
 * What you get: message thread (user ↔ agent bubbles), live "thinking"
 * dots with the latest activity line, a composer (Enter sends), automatic
 * refresh on new messages, job-failure toasts, and cleanup via destroy().
 *
 * Deliberately framework-free DOM (no React) so it works from any module
 * language target. Styles are injected once under `.agc-*` and follow the
 * OS CSS variables, so the control inherits the active theme.
 */
import type { ModuleContext } from "@arclog/module-sdk";

export type ConverseOptions = {
  /**
   * Pin the conversation to one agent. Omit to render an agent selector
   * (defaults to the first fleet member) — the thread follows the choice.
   */
  agentId?: string;
  /** Optional small header title above the thread. */
  title?: string;
  /**
   * Stream the reply in token-by-token as the agent generates it instead
   * of showing the whole bubble at once when the job finishes. DEFAULT ON.
   * Set false for the classic "wait, then the whole reply appears" behavior.
   */
  stream?: boolean;
  /** Fired when the reply job behind a message reaches a terminal state. */
  onJobDone?: (job: { status: string; output?: unknown; reason?: string }) => void;
};

export type ConverseHandle = {
  /** Mount this. It fills its parent (height 100%, flex column). */
  el: HTMLElement;
  /** Re-pull and repaint the thread. */
  refresh(): Promise<void>;
  /** Unsubscribe sockets/timers. Call on unmount. */
  destroy(): void;
};

const STYLE_ID = "arclog-agent-converse-style";

function ensureStyles(): void {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = `
.agc{display:flex;flex-direction:column;height:100%;min-height:0;gap:0;
  color:var(--os-text,#e8e8ed);font:13px var(--os-font-ui,system-ui)}
.agc-head{display:flex;flex-direction:row;align-items:center;gap:8px;
  padding:8px 10px;border-bottom:1px solid rgba(255,255,255,.10)}
.agc-title{font-weight:600;font-size:12px;letter-spacing:.04em;opacity:.75}
.agc-select{border:1px solid rgba(255,255,255,.14);border-radius:8px;background:rgba(0,0,0,.28);
  color:inherit;padding:5px 8px;font:12px var(--os-font-ui,system-ui);color-scheme:dark}
.agc-thread{flex:1 1 auto;min-height:0;overflow:auto;display:flex;flex-direction:column;
  gap:8px;padding:12px 10px}
.agc-bubble{max-width:82%;padding:8px 11px;border-radius:12px;white-space:pre-wrap;
  overflow-wrap:anywhere;line-height:1.45;font-size:12.5px}
.agc-bubble-user{align-self:flex-end;background:rgba(10,132,255,.22);
  border:1px solid rgba(10,132,255,.35)}
.agc-bubble-agent{align-self:flex-start;background:rgba(255,255,255,.06);
  border:1px solid rgba(255,255,255,.12);white-space:normal}
.agc-who{font:600 10px var(--os-font-mono,ui-monospace,monospace);opacity:.55;margin-bottom:3px}
.agc-empty{opacity:.5;text-align:center;padding:24px 12px;font-size:12px;line-height:1.6}
.agc-thinking{display:flex;flex-direction:row;align-items:center;gap:8px;margin:0 10px 8px;
  padding:6px 10px;border-radius:9px;background:rgba(10,132,255,.10);
  border:1px solid rgba(10,132,255,.22)}
.agc-thinking-dots{display:inline-flex;flex-direction:row;gap:4px;align-items:center}
.agc-thinking-dots i{width:6px;height:6px;border-radius:50%;background:var(--os-accent,#0a84ff);
  animation:agc-bounce 1.2s infinite ease-in-out}
.agc-thinking-dots i:nth-child(2){animation-delay:.15s}
.agc-thinking-dots i:nth-child(3){animation-delay:.3s}
@keyframes agc-bounce{0%,60%,100%{transform:translateY(0);opacity:.5}30%{transform:translateY(-4px);opacity:1}}
.agc-thinking-text{font:11px var(--os-font-ui,system-ui);opacity:.75;white-space:nowrap;
  overflow:hidden;text-overflow:ellipsis}
.agc-composer{display:flex;flex-direction:row;gap:8px;padding:10px;
  border-top:1px solid rgba(255,255,255,.10)}
.agc-input{flex:1 1 auto;border:1px solid rgba(255,255,255,.14);border-radius:9px;
  background:rgba(0,0,0,.28);color:inherit;padding:8px 10px;
  font:12.5px var(--os-font-ui,system-ui);color-scheme:dark}
.agc-input:focus{outline:none;border-color:rgba(10,132,255,.6);
  box-shadow:0 0 0 3px rgba(10,132,255,.16)}
.agc-send{border:0;border-radius:9px;background:var(--os-accent,#0a84ff);color:#fff;
  font:600 12px var(--os-font-ui,system-ui);padding:8px 16px;cursor:pointer}
.agc-send:hover{filter:brightness(1.1)}
/* Streaming bubble: a blinking caret marks where the next token lands. */
.agc-bubble-streaming .agc-body::after{content:"▍";opacity:.7;margin-left:1px;
  animation:agc-caret 1s steps(1) infinite}
@keyframes agc-caret{50%{opacity:0}}
/* Agent bubbles carry rendered markdown — block layout inside, not pre-wrap
   (which would stack <p>/<li> margins onto literal newlines). */
.agc-bubble .agc-body p{margin:0 0 6px}
.agc-bubble .agc-body p:last-child{margin-bottom:0}
.agc-bubble .agc-body .md-h{font-weight:650;margin:6px 0 3px;line-height:1.3}
.agc-bubble .agc-body .md-h1{font-size:13.5px}
.agc-bubble .agc-body .md-h2{font-size:12.8px;opacity:.92}
.agc-bubble .agc-body .md-h3{font-size:12.5px;opacity:.85}
.agc-bubble .agc-body .md-ul,.agc-bubble .agc-body .md-ol{margin:3px 0 6px;padding-left:18px}
.agc-bubble .agc-body .md-ul li,.agc-bubble .agc-body .md-ol li{margin:1px 0}
.agc-bubble .agc-body .md-hr{border:0;border-top:1px solid rgba(255,255,255,.14);margin:6px 0}
.agc-bubble .agc-body code{font:11.5px var(--os-font-mono,ui-monospace,monospace);
  background:rgba(0,0,0,.3);border:1px solid rgba(255,255,255,.10);border-radius:4px;padding:0 4px}
`;
  document.head.appendChild(style);
}

export function createAgentConverse(ctx: ModuleContext, opts: ConverseOptions): ConverseHandle {
  ensureStyles();
  const unsubs: Array<() => void> = [];
  let destroyed = false;
  let agentId = opts.agentId || "";
  let thinkingTimer: number | null = null;

  const root = document.createElement("div");
  root.className = "agc";

  /* Header: pinned label or fleet selector */
  const head = document.createElement("div");
  head.className = "agc-head";
  const title = document.createElement("span");
  title.className = "agc-title";
  title.textContent = opts.title || "Agent";
  head.appendChild(title);

  let select: HTMLSelectElement | null = null;
  if (!opts.agentId && ctx.agents) {
    select = document.createElement("select");
    select.className = "agc-select";
    head.appendChild(select);
  }
  root.appendChild(head);

  /* Thread */
  const thread = document.createElement("div");
  thread.className = "agc-thread";
  root.appendChild(thread);

  /* Thinking indicator */
  const thinking = document.createElement("div");
  thinking.className = "agc-thinking";
  thinking.style.display = "none";
  thinking.innerHTML =
    '<span class="agc-thinking-dots"><i></i><i></i><i></i></span><span class="agc-thinking-text"></span>';
  const thinkingText = thinking.querySelector(".agc-thinking-text") as HTMLElement;
  root.appendChild(thinking);

  function setThinking(on: boolean, text?: string): void {
    if (thinkingTimer) {
      window.clearTimeout(thinkingTimer);
      thinkingTimer = null;
    }
    if (on) {
      thinking.style.display = "flex";
      if (text) thinkingText.textContent = text;
      // Safety net: dots never spin forever if events stop arriving.
      thinkingTimer = window.setTimeout(() => setThinking(false), 120_000);
    } else {
      thinking.style.display = "none";
      thinkingText.textContent = "";
    }
  }

  /* Composer */
  const composer = document.createElement("div");
  composer.className = "agc-composer";
  const input = document.createElement("input");
  input.className = "agc-input";
  input.placeholder = "Message the agent…";
  const send = document.createElement("button");
  send.type = "button";
  send.className = "agc-send";
  send.textContent = "Send";
  composer.append(input, send);
  root.appendChild(composer);

  async function paint(): Promise<void> {
    if (destroyed || !ctx.agents) return;
    thread.innerHTML = "";
    const msgs = await ctx.agents.messages(agentId || undefined).catch(() => []);
    if (!msgs.length) {
      const e = document.createElement("div");
      e.className = "agc-empty";
      e.textContent = agentId
        ? "No conversation yet. Say something below — the agent answers here."
        : "No conversations yet.";
      thread.appendChild(e);
    }
    for (const m of msgs.slice(-60)) {
      const mine = m.kind === "user";
      const bubble = document.createElement("div");
      bubble.className = `agc-bubble ${mine ? "agc-bubble-user" : "agc-bubble-agent"}`;
      const who = document.createElement("div");
      who.className = "agc-who";
      who.textContent = mine ? "you" : m.from.replace(/^agent:/, "");
      const body = document.createElement("div");
      body.className = "agc-body";
      if (mine) {
        // User text stays literal — what the user typed is what shows.
        body.textContent = m.text;
      } else {
        // Agent replies are markdown — render them (escape-first, safe).
        body.innerHTML = ctx.ui?.markdown ? ctx.ui.markdown(m.text) : "";
        if (!ctx.ui?.markdown) body.textContent = m.text; // belt + braces
      }
      bubble.append(who, body);
      thread.appendChild(bubble);
    }
    window.setTimeout(() => {
      thread.scrollTop = thread.scrollHeight;
    }, 0);
  }

  async function loadFleet(): Promise<void> {
    if (!ctx.agents) return;
    const fleet = await ctx.agents.list().catch(() => []);
    if (opts.agentId) {
      const a = fleet.find((x) => x.id === opts.agentId);
      title.textContent = opts.title || a?.label || opts.agentId;
      agentId = opts.agentId;
      return;
    }
    if (!select) return;
    select.innerHTML = "";
    for (const a of fleet) {
      const o = document.createElement("option");
      o.value = a.id;
      o.textContent = a.label;
      select.appendChild(o);
    }
    if (fleet.length) {
      agentId = fleet[0].id;
      select.value = agentId;
      select.addEventListener("change", () => {
        agentId = select!.value;
        void paint();
      });
    }
  }

  async function doSend(): Promise<void> {
    const text = input.value.trim();
    if (!text || !agentId || !ctx.agents) return;
    input.value = "";
    setThinking(true, "sending…");
    void paint(); // my bubble appears once the server records it
    const streamOn = opts.stream !== false; // default ON
    try {
      const jobId = await ctx.agents.message(agentId, text, { stream: streamOn });
      void paint();
      if (!jobId) {
        setThinking(false);
        ctx.notify?.warn?.("The agent didn't pick up — try again.");
        return;
      }
      const offProgress = ctx.agents.subscribeProgress((p) => setThinking(true, p.text), jobId);

      // STREAMING: grow a live bubble token-by-token as agent.delta events
      // arrive. The final inbox append (agent.message) repaints the thread
      // and swaps the live bubble for the canonical one — deltas lost to a
      // subscribe race never matter because the reply lands whole anyway.
      let liveBody: HTMLElement | null = null;
      let streamed = "";
      let offDelta: (() => void) | null = null;
      if (streamOn) {
        offDelta = ctx.agents.subscribeDelta((d) => {
          if (!liveBody && d.done) return; // nothing streamed before the terminator
          if (!liveBody) {
            setThinking(false); // the caret replaces the dots
            const bubble = document.createElement("div");
            bubble.className = "agc-bubble agc-bubble-agent agc-bubble-streaming";
            const who = document.createElement("div");
            who.className = "agc-who";
            who.textContent = d.agentId;
            liveBody = document.createElement("div");
            liveBody.className = "agc-body";
            bubble.append(who, liveBody);
            thread.appendChild(bubble);
          }
          streamed += d.text;
          // Live bubble: re-render the accumulated markdown each delta (the
          // renderer is cheap and escape-first, so partial markdown mid-token
          // degrades to visible text, never to injected markup).
          if (ctx.ui?.markdown) liveBody.innerHTML = ctx.ui.markdown(streamed);
          else liveBody.textContent = streamed;
          thread.scrollTop = thread.scrollHeight;
        }, jobId);
      }

      const off = ctx.agents.subscribe(jobId, (job) => {
        if (job.status === "succeeded" || job.status === "failed") {
          off();
          offProgress();
          offDelta?.();
          setThinking(false);
          if (streamed && liveBody) {
            // Strip the caret; keep the text we streamed until paint swaps it.
            liveBody.parentElement?.classList.remove("agc-bubble-streaming");
          }
          void paint(); // the canonical reply bubble lands in the thread
          opts.onJobDone?.({ status: job.status, output: job.output, reason: job.reason });
          if (job.status === "failed") {
            ctx.notify?.warn?.(`Agent job failed: ${job.reason || "unknown"}`);
          }
        }
      });
    } catch (e) {
      setThinking(false);
      ctx.notify?.warn?.(`Could not reach the agent: ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  send.addEventListener("click", () => void doSend());
  input.addEventListener("keydown", (e) => {
    if (e.key === "Enter") void doSend();
  });

  /* Live: any new message repaints (the WS push keeps threads current). */
  if (ctx.agents) {
    unsubs.push(ctx.agents.subscribeMessages(() => void paint()));
  }

  void loadFleet().then(() => void paint());

  return {
    el: root,
    refresh: paint,
    destroy(): void {
      destroyed = true;
      if (thinkingTimer) window.clearTimeout(thinkingTimer);
      for (const u of unsubs) u();
    },
  };
}
